import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { Flex, Text } from '@mantine/core';

import { useSettingsStore } from '@/shared/store/settings.store.ts';
import { TDateRange } from '@/shared/types/app.types';

import { RangeDatePicker } from '../RangeDatePicker';
import { RangeTimePicker } from '../RangeTimePicker';
import { TimeSliderPicker } from '../TimeSliderPicker/TimeSliderPicker';

interface DateTimePickerProps {
	value?: TDateRange;
	defaultTime: Date | null;
	disabledDates?: {
		minDate: Date;
		maxDate: Date;
	};
	onlyTime?: boolean;
	isTaxi?: boolean;
	withTimeSlider?: boolean;
	onChange?: (date: TDateRange) => void;
}

export const DateTimePicker = ({
	value,
	disabledDates,
	defaultTime,
	onlyTime = false,
	isTaxi = false,
	withTimeSlider = true,
	onChange,
}: DateTimePickerProps) => {
	const workTime = useSettingsStore((state) => state.workTime);
	const [pickerValue, setPickerValue] = useState<TDateRange>(value ?? [null, null]);

	const handleChange = (date: TDateRange) => {
		setPickerValue(date);
		if (onChange) {
			onChange(date);
		}
	};

	const handleAddTime = (minutes: number, isFullDay: boolean = false) => {
		if (!onChange) return null;

		const newTime = pickerValue;
		// TODO: test
		// newTime[1] =
		// 	dayjs(pickerValue[0]).date() === dayjs(pickerValue[1]).date() ? defaultTime : pickerValue[1];
		onChange(newTime);

		const getEndTime = () =>
			dayjs(pickerValue[1] ?? defaultTime)
				.hour(dayjs(pickerValue[0] ?? defaultTime).hour())
				.minute(dayjs(pickerValue[0] ?? defaultTime).minute())
				.second(0);

		if (isFullDay) {
			newTime[1] = dayjs(newTime[1])
				.set('hours', dayjs(workTime![1]).hour())
				.set('minute', dayjs(workTime![1]).minute())
				.toDate();
			onChange(newTime);
			return;
		}

		if (minutes) {
			newTime[1] = getEndTime().add(minutes, 'minute').toDate();

			onChange(newTime);
			return;
		}

		newTime[1] = getEndTime().add(1, 'hours').toDate();
		onChange(newTime);
	};

	useEffect(() => {
		if (value) {
			setPickerValue(value);
		}
	}, [isTaxi, value]);

	return (
		<Flex direction="column" gap={16}>
			<Text fz={20} fw={800} lh="26px">
				Дата и время
			</Text>
			<Flex gap={8} justify="space-between">
				<Flex direction="column" w={'100%'}>
					<RangeDatePicker
						value={pickerValue}
						onChange={handleChange}
						disabled={onlyTime}
						{...disabledDates}
					/>
				</Flex>
				<Flex direction="column" w={'100%'}>
					<RangeTimePicker isTaxi={isTaxi} value={pickerValue} onChange={handleChange} />
				</Flex>
			</Flex>
			{withTimeSlider && (
				<TimeSliderPicker setTime={handleAddTime} disabled={pickerValue.every((date) => !date)} />
			)}
		</Flex>
	);
};
